/**
 * Tech App Preview shell support.
 *
 * The admin "Tech App Preview" page embeds the technician app in a same-origin
 * iframe with a short-lived preview token:
 *
 *   /technician?tpv=1#tp=<preview_access_token>
 *
 * A same-origin iframe SHARES localStorage/sessionStorage/cookies with the
 * admin tab, so a naive "log in as the tech inside the iframe" would clobber
 * the admin's own session. This module makes the iframe fully isolated:
 *
 *   1. Detects preview mode from the URL (runs at import time, BEFORE any
 *      other app code touches storage — it is the FIRST import in main.js).
 *   2. Replaces window.localStorage + window.sessionStorage with in-memory
 *      shims → nothing the tech app writes can leak into the admin session,
 *      and nothing of the admin session leaks into the tech view.
 *   3. Exposes the preview token/mode/context to tokenManager, the api
 *      client, main.js and the technician shell.
 *
 * The token itself never touches persistent storage: it lives in this module
 * (and tokenManager's in-memory ref). Refresh is impossible by design — a
 * refresh inside the iframe would ride the ADMIN's httpOnly cookie and turn
 * the preview into an admin session. When the token expires the iframe posts
 * 'tech_preview_unauthorized' to the parent page, which offers a restart.
 */
import axios from 'axios'

let _state = null

function _makeMemoryStorage(seed = {}) {
  const m = new Map(Object.entries(seed))
  return {
    get length() { return m.size },
    key(i) { return Array.from(m.keys())[i] ?? null },
    getItem(k) { return m.has(String(k)) ? m.get(String(k)) : null },
    setItem(k, v) { m.set(String(k), String(v)) },
    removeItem(k) { m.delete(String(k)) },
    clear() { m.clear() },
  }
}

function _init() {
  try {
    if (typeof window === 'undefined') return
    const search = window.location.search || ''
    if (!/[?&]tpv=1(&|$)/.test(search)) return
    const hash = window.location.hash || ''
    const m = hash.match(/tp=([^&]+)/)
    if (!m) return

    // Optional interface version override (builder Live Preview): render a
    // SPECIFIC version — even an unpublished draft — with this tech's data.
    const ifaceMatch = search.match(/[?&]iface=(\d+)/)

    _state = {
      token: decodeURIComponent(m[1]),
      mode: 'readonly',
      context: null,
      ifaceVersion: ifaceMatch ? Number(ifaceMatch[1]) : null,
    }

    // Strip the token from the visible URL immediately (it must not linger in
    // history / be copyable from the address bar of "open frame in new tab").
    try {
      window.history.replaceState(null, '', window.location.pathname + window.location.search)
    } catch (e) { /* ignore */ }

    // Full storage isolation from the admin tab (same-origin iframe shares both).
    try {
      Object.defineProperty(window, 'localStorage', {
        value: _makeMemoryStorage({ is_technician_session: 'true' }),
        configurable: true,
      })
      Object.defineProperty(window, 'sessionStorage', {
        value: _makeMemoryStorage(),
        configurable: true,
      })
    } catch (e) {
      console.warn('[TechPreview] storage shim failed:', e?.message || e)
    }

    window.__TECH_PREVIEW__ = _state
    console.log('[TechPreview] preview shell active')
  } catch (e) {
    console.warn('[TechPreview] init failed:', e?.message || e)
  }
}

_init()

export function isTechPreview() {
  return !!_state
}

export function previewToken() {
  return _state?.token || null
}

export function previewMode() {
  return _state?.mode || 'readonly'
}

export function previewContext() {
  return _state?.context || null
}

/** Interface version id forced by the builder Live Preview (or null). */
export function previewInterfaceVersion() {
  return _state?.ifaceVersion || null
}

export function notifyParent(type, payload = {}) {
  try {
    window.parent?.postMessage({ source: 'tech-preview', type, ...payload }, window.location.origin)
  } catch (e) { /* ignore */ }
}

/**
 * Fetch the auth-style context for the preview token and seed the (shimmed)
 * storage exactly like a technician login would, so the whole tech app —
 * auth store hydration, router guard, direct localStorage readers — behaves
 * as if the technician had logged in on this "device".
 * Called from main.js BEFORE the app mounts.
 */
export async function bootstrapPreviewSession() {
  if (!_state) return null
  const res = await axios.get('/api/v1/fsa/tech-preview/context/', {
    headers: { Authorization: `Bearer ${_state.token}`, 'X-Tech-Preview': '1' },
    timeout: 20000,
  })
  const ctx = res.data
  _state.mode = ctx?.preview?.mode || _state.mode
  _state.context = ctx

  // Writes go to the in-memory shim — invisible to the admin tab.
  localStorage.setItem('user', JSON.stringify(ctx.user))
  localStorage.setItem('domain', JSON.stringify(ctx.domain))
  localStorage.setItem('permissions', JSON.stringify(ctx.permissions || []))
  localStorage.setItem('is_technician_session', 'true')
  localStorage.setItem('interface_version_id', ctx.interface_version || '')

  notifyParent('tech_preview_ready', {
    mode: _state.mode,
    technician: ctx.user ? `${ctx.user.first_name || ''} ${ctx.user.last_name || ''}`.trim() : '',
    // The exact timezone the emulated app renders timestamps in, so the admin
    // page can label it OUTSIDE the phone (it can differ from the admin's own).
    timezone: ctx.user?.timezone
      || ctx.domain?.default_timezone
      || ctx.domain?.timezone
      || 'America/Toronto',
  })
  return ctx
}
